import React, { useState } from 'react';
import { 
  Gamepad2, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  ArrowRight, 
  RotateCcw,
  Sparkles
} from 'lucide-react';

export const ScamSimulatorPage: React.FC = () => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<'scam' | 'legit' | null>(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  const scenarios = [
    {
      channel: 'SMS Message',
      sender: 'myGov',
      content: 'myGov: You have an outstanding tax refund of $1,420.50 AUD pending. Claim your payment now: https://mygov-portal-tax-return.net',
      isScam: true,
      explanation: 'SCAM. myGov and the ATO never include clickable hyperlinks in transactional SMS. The domain .net is non-government.'
    },
    {
      channel: 'SMS Message',
      sender: 'CommBank',
      content: 'CommBank: NetCode 891240 for transfer of $250.00 to J SMITH. If you did not request this, call 132221 immediately.',
      isScam: false,
      explanation: 'LEGITIMATE. This is a standard transactional security code. It does not ask you to click a link or reply with the code.'
    },
    {
      channel: 'Facebook Marketplace',
      sender: 'Buyer: Dave K.',
      content: 'I will buy your couch for $400. I am away for mining work but my courier will collect it. I have sent PayID payment. Please check your email to upgrade your PayID account to business tier.',
      isScam: true,
      explanation: 'SCAM. PayID has no business upgrades or deposit fees. Scammers forge fake payment confirmation emails.'
    },
    {
      channel: 'SMS Message',
      sender: '+61 412 990 128',
      content: 'Hi mum, I dropped my phone in the toilet. This is my temporary new number. Can you message me on WhatsApp urgently? I need help with rent.',
      isScam: true,
      explanation: 'SCAM. The classic "Hi Mum" family emergency scam. Always call your child on their known existing number before transferring money.'
    },
    {
      channel: 'Email Message',
      sender: 'support@linkt.com.au',
      content: 'Your Linkt auto-toll account was successfully topped up with $40.00 via direct debit on 18 Aug 2026. View your monthly tax invoice inside the Linkt app.',
      isScam: false,
      explanation: 'LEGITIMATE. Notice sent from authentic domain without urgent threats or payment redirection links.'
    }
  ];

  const current = scenarios[currentIdx];

  const handleChoice = (choice: 'scam' | 'legit') => {
    setSelectedAnswer(choice);
    const isCorrect = (choice === 'scam' && current.isScam) || (choice === 'legit' && !current.isScam);
    if (isCorrect) setScore(prev => prev + 1);
  };

  const handleNext = () => {
    if (currentIdx < scenarios.length - 1) {
      setCurrentIdx(prev => prev + 1);
      setSelectedAnswer(null);
    } else {
      setCompleted(true);
    }
  };

  const restart = () => {
    setCurrentIdx(0);
    setSelectedAnswer(null);
    setScore(0);
    setCompleted(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-950 text-amber-300 text-xs font-bold border border-amber-800">
          <Gamepad2 className="w-3.5 h-3.5" />
          <span>INTERACTIVE CYBER DEFENCE SIMULATOR</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white">Australian Scam Spotting Simulator</h1>
        <p className="text-xs text-slate-400 max-w-lg mx-auto">
          Test your instinct against real Australian scam lures. Can you separate authentic bank NetCodes from criminal phishing traps?
        </p>
      </div>

      {!completed ? (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl text-xs">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 text-slate-400">
            <span>Scenario {currentIdx + 1} of {scenarios.length}</span>
            <span className="font-mono text-emerald-400 font-bold">Score: {score} Correct</span>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between text-slate-400">
              <span className="font-bold text-white uppercase">{current.channel}</span>
              <span className="font-mono">Sender: {current.sender}</span>
            </div>

            <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 text-slate-200 text-sm font-mono leading-relaxed shadow-inner">
              "{current.content}"
            </div>
          </div>

          {selectedAnswer === null ? (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <button
                onClick={() => handleChoice('scam')}
                className="py-3.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-sm shadow-lg transition-colors flex items-center justify-center gap-2"
              >
                <AlertTriangle className="w-4 h-4" />
                <span>THIS IS A SCAM</span>
              </button>
              <button
                onClick={() => handleChoice('legit')}
                className="py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-lg transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>THIS IS LEGITIMATE</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4 animate-fade-in">
              <div className={`p-4 rounded-2xl border ${
                (selectedAnswer === 'scam' && current.isScam) || (selectedAnswer === 'legit' && !current.isScam)
                  ? 'bg-emerald-950/40 border-emerald-700 text-emerald-200'
                  : 'bg-rose-950/40 border-rose-700 text-rose-200'
              }`}>
                <div className="font-bold text-white text-sm mb-1 flex items-center gap-2">
                  {(selectedAnswer === 'scam' && current.isScam) || (selectedAnswer === 'legit' && !current.isScam) ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-rose-400" />
                  )}
                  <span>{(selectedAnswer === 'scam' && current.isScam) || (selectedAnswer === 'legit' && !current.isScam) ? 'Correct Assessment!' : 'Incorrect.'}</span>
                </div>
                <p className="leading-relaxed text-xs">{current.explanation}</p>
              </div>

              <button
                onClick={handleNext}
                className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-2"
              >
                <span>Continue to Next Scenario</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center space-y-6 shadow-2xl animate-fade-in">
          <Sparkles className="w-12 h-12 text-emerald-400 mx-auto" />
          <div className="space-y-1">
            <h3 className="text-2xl font-extrabold text-white">Simulation Completed!</h3>
            <p className="text-sm text-slate-300">
              You scored <span className="font-bold text-emerald-400 font-mono text-base">{score} out of {scenarios.length}</span> correct.
            </p>
          </div>

          <button
            onClick={restart}
            className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs transition-colors inline-flex items-center gap-2"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Retake Simulator</span>
          </button>
        </div>
      )}
    </div>
  );
};
